import Util from './Util';
import {smartFetch} from './smartFetch'; // Contains USE_MOCKS flag

const OPTIMAL_PAGE_SIZE = 2000; // 500;

function buildPaginationQuery(page, size) {
    return `&page=${page}&size=${size}`;
}

async function fetchAssets(url, abortSignal = null) {
    try {
        const fetchPromise = smartFetch(url);

        if (abortSignal) {
            // Race between fetch and abort
            const abortPromise = new Promise((_, reject) => {
                if (abortSignal.aborted) {
                    reject(new Error('Request aborted'));
                }
                abortSignal.addEventListener('abort', () => {
                    reject(new Error('Request aborted'));
                });
            });

            const data = await Promise.race([fetchPromise, abortPromise]);
            if (data && typeof data === 'object' && 'content' in data) {
                return data;
            }
        } else {
            const data = await fetchPromise;
            if (data && typeof data === 'object' && 'content' in data) {
                return data;
            }
        }

        console.warn('No data received'); // eslint-disable-line
        return Error('No data received');
    } catch (error) {
        if (error.message === 'Request aborted') {
            return Error('Request aborted');
        }
        console.warn('Error fetching folder page:', error); // eslint-disable-line
        return Error('Failed to fetch folder page');
    }
}

export async function fetchFolderPage(folderId, page = 0, size = 50, abortSignal = null) {
    const url = `${Util.ROOT_PATH}/getFolder?libraryId=${folderId}${buildPaginationQuery(page, size)}`;
    const data = await fetchAssets(url, abortSignal);
    return data;
}

export async function fetchTagPage(tag, page = 0, size = 50, abortSignal = null) {
    const url = `${Util.ROOT_PATH}/getMedia?tags=${Util.safeURIencode(tag)}${buildPaginationQuery(page, size)}`;
    const data = await fetchAssets(url, abortSignal);
    return data;
}

export async function fetchAllFolderAssets(folderId, abortSignal = null) {
    const startTime = performance.now();
    let allAssets = [];
    try {
        let totalPages = 1;

        // First fetch to get total pages
        const firstFetchStart = performance.now();
        const firstData = await fetchFolderPage(folderId, 0, OPTIMAL_PAGE_SIZE, abortSignal);
        const firstFetchEnd = performance.now();
        console.log(`First page fetch took ${(firstFetchEnd - firstFetchStart).toFixed(2)}ms`); // eslint-disable-line

        if (firstData instanceof Error) {
            return firstData;
        }

        allAssets = allAssets.concat(firstData.content || []);
        totalPages = firstData.totalPages || 1;

        // If there are more pages, fetch them all in parallel
        if (totalPages > 1) {
            const parallelFetchStart = performance.now();
            const promises = [];
            for (let i = 1; i < totalPages; i += 1) {
                promises.push(fetchFolderPage(folderId, i, OPTIMAL_PAGE_SIZE, abortSignal));
            }

            const results = await Promise.all(promises);
            const parallelFetchEnd = performance.now();
            // eslint-disable-next-line no-console
            console.log(
                `Parallel fetch of ${totalPages - 1} pages took ${(parallelFetchEnd - parallelFetchStart).toFixed(2)}ms`
            );

            for (let i = 0; i < results.length; i += 1) {
                const data = results[i];
                if (data instanceof Error) {
                    return data;
                }
                allAssets = allAssets.concat(data.content || []);
            }
        }

        const endTime = performance.now();
        // eslint-disable-next-line no-console
        console.log(
            `fetchAllFolderAssets completed: ${allAssets.length} assets from ${totalPages} pages in ${(endTime - startTime).toFixed(2)}ms`
        );
    } catch (error) {
        if (error.name === 'AbortError' || (error.message && error.message.includes('aborted'))) {
            return new Error('Request aborted');
        }
        console.warn('Error fetching all folder assets:', error); // eslint-disable-line
        return new Error('Failed to load folder assets');
    }
    return allAssets;
}

export async function fetchAllTagAssets(tag, abortSignal = null) {
    const startTime = performance.now();
    let allAssets = [];
    try {
        let totalPages = 1;

        // First fetch to get total pages
        const firstFetchStart = performance.now();
        const firstData = await fetchTagPage(tag, 0, OPTIMAL_PAGE_SIZE, abortSignal);
        const firstFetchEnd = performance.now();
        console.log(`First page fetch took ${(firstFetchEnd - firstFetchStart).toFixed(2)}ms`); // eslint-disable-line

        if (firstData instanceof Error) {
            return firstData;
        }

        allAssets = allAssets.concat(firstData.content || []);
        totalPages = firstData.totalPages || 1;

        // If there are more pages, fetch them all in parallel
        if (totalPages > 1) {
            const parallelFetchStart = performance.now();
            const promises = [];
            for (let i = 1; i < totalPages; i += 1) {
                promises.push(fetchTagPage(tag, i, OPTIMAL_PAGE_SIZE, abortSignal));
            }

            const results = await Promise.all(promises);
            const parallelFetchEnd = performance.now();
            // eslint-disable-next-line no-console
            console.log(
                `Parallel fetch of ${totalPages - 1} pages took ${(parallelFetchEnd - parallelFetchStart).toFixed(2)}ms`
            );

            for (let i = 0; i < results.length; i += 1) {
                const data = results[i];
                if (data instanceof Error) {
                    return data;
                }
                allAssets = allAssets.concat(data.content || []);
            }
        }

        const endTime = performance.now();
        // eslint-disable-next-line no-console
        console.log(
            `fetchAllTagAssets completed: ${allAssets.length} assets from ${totalPages} pages in ${(endTime - startTime).toFixed(2)}ms`
        );
    } catch (error) {
        if (error.name === 'AbortError' || (error.message && error.message.includes('aborted'))) {
            return new Error('Request aborted');
        }
        console.warn('Error fetching all tag assets:', error); // eslint-disable-line
        return new Error('Failed to load tag assets');
    }
    return allAssets;
}
